import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { Tooltip } from 'bootstrap';
import { useNavigate } from 'react-router-dom';
import { auto } from '@popperjs/core';
import { useTranslation } from 'react-i18next';

const statusOrder = ['Draft', 'Active', 'Clôturé'];
const statusLabels = {
  Draft: 'Brouillons',
  Active: 'Actifs',
  Clôturé: 'Terminés'
};

const itemsPerPage = 4;

const Production = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [projects, setProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [viewMode, setViewMode] = useState('kanban');
  const [listPage, setListPage] = useState(1);
  const [kanbanPages, setKanbanPages] = useState({
    Draft: 1,
    Active: 1,
    Clôturé: 1,
  });
  const tokenParsed = keycloak?.tokenParsed || {};
  const userName =
    keycloak?.tokenParsed?.preferred_username ||
    keycloak?.tokenParsed?.given_name ||
    'utilisateur';


  const groups = tokenParsed?.groups || [];
  const normalizedGroups = groups?.map((g) => g.replace(/^\//, '')) || [];
  const isMaintainer = normalizedGroups.includes('maintainer');
  const isScreenwriter = normalizedGroups.includes('screenwriter');
  const isContributor = normalizedGroups.includes('contributor');
  useEffect(() => {
    fetch('https://www.weavevo.com/projects')
      .then((res) => res.json())
      .then((data) => setProjects(data))
      .catch((err) => console.error('Erreur chargement projets :', err));
  }, []);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((el) => new Tooltip(el));
  }, [projects, viewMode]);

  const groupByStatus = () => {
    const grouped = {};
    statusOrder.forEach((status) => (grouped[status] = []));
    projects.forEach((project) => {
      const key = project.status || 'Draft';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(project);
    });
    return grouped;
  };

  const groupedProjects = groupByStatus();

  const handleEdit = (id) => navigate(`/wizard?edit=true&id=${id}`);
  const handleShow = (id) => navigate(`/project/${id}/manage`);
  const handleRecap = (id) => navigate(`/projects/${id}/recap`);

  const renderProjectActions = (project) => {
    const isOwner = project.created_by === userName;
    const status = project.status || 'Draft';
    const actions = [];

    if (status === 'Draft' && isOwner) {
      actions.push(
        <button
          key="edit"
          className="btn btn-outline-primary btn-sm rounded-pill"
          data-bs-toggle="tooltip"
          title="Modifier le scénario"
          onClick={() => handleEdit(project.id)}
        >
          ✏️ Modifier le scénario
        </button>
      );
    }

    if (status === 'Active') {
  actions.push(
    <button
      key="contribute"
      className="btn btn-outline-success btn-sm rounded-pill"
      data-bs-toggle="tooltip"
      title="Contribuer à ce documentaire"
      onClick={() => window.location.href = `/project/${project.id}`}
    >
      ➕ Contribuer
    </button>
  );

  if (isMaintainer || isScreenwriter) {
    actions.push(
      <button
        key="studio"
        className="btn btn-outline-info btn-sm rounded-pill"
        data-bs-toggle="tooltip"
        title="Vers le Studio"
        onClick={() => handleShow(project.id)}
      >
        🎬 Vers le Studio
      </button>
    );
  }
}


    if (status === 'Clôturé') {
      actions.push(
        <button
          key="recap"
          className="btn btn-outline-info btn-sm rounded-pill"
          data-bs-toggle="tooltip"
          title="Voir le récapitulatif"
          onClick={() => handleRecap(project.id)}
        >
          🧾 Récapitulatif
        </button>
      );
    }

    if (actions.length === 0) {
      return <span className="text-muted small">🔒 Lecture seule</span>;
    }

    return <div className="d-flex gap-2 justify-content-center flex-wrap">{actions}</div>;
  };

  const filteredProjects = projects.filter((project) => {
    const status = project.status || 'Draft';
    return activeFilter === 'All' || status === activeFilter;
  });

  const paginate = (array, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    return array.slice(startIndex, startIndex + itemsPerPage);
  };

  const formatName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <div className="container py-5">
      <div className="mb-5 text-center">
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <div className="d-flex flex-wrap gap-2">
          <button
            className={`btn btn-sm rounded-pill ${activeFilter === 'All' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveFilter('All')}
          >
            Tous
          </button>
          {statusOrder.map((status) => (
            <button
              key={status}
              className={`btn btn-sm rounded-pill ${activeFilter === status ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveFilter(status)}
            >
              {statusLabels[status]}
            </button>
          ))}
        </div>

        <div className="mt-2 mt-md-0">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setViewMode(viewMode === 'kanban' ? 'list' : 'kanban')}
          >
            {viewMode === 'kanban' ? '🔁 Vue Liste' : '🧩 Vue Kanban'}
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        // Vue liste (non modifiée)
        <>
          <div className="table-responsive">
            <table className="table table-hover table-striped align-middle">
              <thead className="table-light">
                <tr style={{ height: '70px' }}>
                  <th>Titre</th>
                  <th>Description</th>
                  <th>Statut</th>
                  <th>Date</th>
                  <th className="text-center" style={{ minWidth: '220px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginate(filteredProjects, listPage).map((project) => (
                  <tr key={project.id}>
                    <td className="fw-semibold text-primary">{project.title}</td>
                    <td
                      className="text-muted small text-truncate"
                      style={{ maxWidth: '200px' }}
                      title={project.description}
                    >
                      {project.description}
                    </td>
                    <td>
                      <span className="badge bg-secondary">
                        {project.status || 'Draft'}
                      </span>
                    </td>
                    <td>{new Date(project.created_at).toLocaleDateString()}</td>
                    <td>{renderProjectActions(project)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        // Vue Kanban modifiée
        <div className="row g-4">
          {statusOrder.map((status) => {
            if (activeFilter !== 'All' && activeFilter !== status) return null;
            const projectsInStatus = groupedProjects[status] || [];
            const currentKanbanPage = kanbanPages[status];
            const paginated = paginate(projectsInStatus, currentKanbanPage);

            return (
              <div className="col-md-4" key={status}>
                <div className="bg-white shadow-sm border rounded-4 p-4 h-100 d-flex flex-column">
                  <h5 className="text-center mb-3 fw-bold">{statusLabels[status]}</h5>
                  {paginated.length === 0 ? (
                    <p className="text-muted text-center">Aucun projet</p>
                  ) : (
                    paginated.map((project) => {
                      const videoUrl = `https://www.weavevo.com/output/project_${project.id}_final.mp4`;

                      return (
                        <div
                          key={project.id}
                          className="card shadow-sm mb-3 border-0 rounded-4 p-3 bg-light d-flex flex-column justify-content-between"
                          style={{ minHeight: '200px' }}
                        >
                          <div>
                            <h6 className="fw-semibold text-primary">{project.title}</h6>
                            <p className="text-muted small mb-2">{project.description}</p>

                            {project.video_url || project.status === 'Clôturé' ? (
                              <video className="w-100 rounded mb-2" controls style={{ maxHeight: '200px', objectFit: 'cover' }}>
                                <source src={project.video_url || `https://www.weavevo.com/output/project_${project.id}_final.mp4`} type="video/mp4" />
                                Votre navigateur ne supporte pas la lecture de vidéos.
                              </video>
                            ) : (
                              <div
                                className={`w-100 text-center text-muted border border-dashed rounded p-4 mb-2 ${project.status === 'Active' ? 'pulse-bg' : ''}`}
                                style={{
                                  backgroundColor: project.status === 'Active' ? '#e6f0ff' : '#f8f9fa',
                                  backgroundImage: project.status === 'Active' ? 'url("https://cdn-icons-png.flaticon.com/512/929/929566.png")' : 'none',
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'center',
                                  backgroundSize: '50px',
                                  height: '150px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontStyle: 'italic'
                                }}
                              >
                                {project.status === 'Draft' ? '📝 Scénario en cours de préparation...' : '🎬 Montage en cours...'}
                              </div>
                            )}

                          </div>
                          <div className="d-flex flex-column gap-2">
                            {renderProjectActions(project)}
                            <small className="text-muted">
                              Créé par {formatName(project.created_by || "un utilisateur")} le 📅 {new Date(project.created_at).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {projectsInStatus.length > itemsPerPage && (
                    <div className="mt-3 d-flex justify-content-center">
                      <nav>
                        <ul className="pagination pagination-sm">
                          {Array.from({ length: Math.ceil(projectsInStatus.length / itemsPerPage) }, (_, i) => (
                            <li key={i + 1} className={`page-item ${kanbanPages[status] === i + 1 ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setKanbanPages({ ...kanbanPages, [status]: i + 1 })}
                              >
                                {i + 1}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Production;
